import { ReactNode, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BBox as BBoxType } from 'geojson';
import { point } from '@turf/helpers';
import distance from '@turf/distance';
import circle from '@turf/circle';
import bbox from '@turf/bbox';
import { Report } from 'features/reports/report.types';
import { Wildfire, WildfireListItem } from 'features/wildfires/wildfire.types';
import { Flooding, FloodingListItem } from 'features/flooding/flooding.types';
import { Hazard } from 'features/hazards/hazard.types';
import {
  DEFAULT_VIEW_STATE,
  ZOOM_LEVELS,
} from 'features/mapping/mapping.constants';
import { MapType } from 'features/mapping/mapTypePolicy';
import { API } from 'api';
import { useCacheState } from 'state/useCacheState';
import { useMapState } from 'state/useMapState';
import { IncidentMapStateUpdate } from 'features/mapping/mapping.types';
import {
  FitBoundsOptions,
  LngLat,
  LngLatBoundsFromLngLat,
} from 'shared/map-exports';
import { Map } from 'features/mapping/components/Map';
import { AxiosResponse } from 'axios';
import { ReportsLayer } from 'features/mapping/components/layers/ReportsLayer';
import {
  ActiveFloodingsLayer,
  InactiveFloodingsLayer,
} from 'features/mapping/components/layers/FloodingsLayer';
import { WildfiresLayer } from 'features/mapping/components/layers/WildfiresLayer';
import { getReportMapVisibilityHours } from 'features/mapping/icons/icons';
import { isRingReport } from 'features/reports/reports.utils';

type GeoEventReportsMapProps = {
  geoEvent: Wildfire | Flooding | Hazard;
  hiddenMapPixels: number | null;
  mapType: MapType;
  style?: React.CSSProperties;
  noMarkers?: boolean;
  children?: ReactNode;
  reportId?: number;
  showSelectedGeoEventLabel?: boolean;
  zoom?: number;
};

type CoordsPoint = [number, number];

const fetchReports = (
  geoEventId: number,
  cacheBusterTs: number | null,
): Promise<AxiosResponse<Report[]>> => {
  let baseUrl = `reports/?geo_event_id=${geoEventId}&status=approved&has_lat_lng=true`;
  if (cacheBusterTs) {
    baseUrl += `&ts=${cacheBusterTs}`;
  }
  return API.get<Report[]>(baseUrl);
};

const getFarthestDistance = (
  center: CoordsPoint,
  coords: CoordsPoint[],
): number => {
  let farthestDistance = -1;

  coords.forEach((coord) => {
    const distanceKm = distance(point(center), point(coord));

    if (distanceKm > farthestDistance) {
      farthestDistance = distanceKm;
    }
  });

  return farthestDistance;
};

const calculateBoundingBox = (
  center: CoordsPoint,
  radius: number,
): BBoxType => {
  const circlePolygon = circle(center, radius);
  const boundingBox = bbox(circlePolygon);
  return boundingBox;
};

export const GeoEventReportsMap = (
  props: GeoEventReportsMapProps,
): JSX.Element => {
  const {
    geoEvent,
    mapType,
    style,
    hiddenMapPixels,
    noMarkers,
    children,
    reportId,
    showSelectedGeoEventLabel = false,
    zoom,
  } = props;
  const { id: geoEventId } = geoEvent;
  const { reportMapState, updateReportMapState, updateGeoEventMapState } =
    useMapState();
  const { cacheBusterTs } = useCacheState();

  useEffect(() => {
    updateGeoEventMapState({ lat: geoEvent.lat, lng: geoEvent.lng });
  }, [geoEvent.lat, geoEvent.lng, updateGeoEventMapState]);

  const { data, isLoading } = useQuery({
    queryKey: ['reports', geoEventId, cacheBusterTs],
    queryFn: () => fetchReports(geoEventId, cacheBusterTs),
  });
  const reports = useMemo(() => {
    if (!isLoading && data) {
      const { data: reportsData } = data;
      return reportsData;
    }
    return [];
  }, [data, isLoading]);

  const onViewportChange = useCallback(
    (viewport: IncidentMapStateUpdate): void => {
      updateReportMapState({ currentGeoEventId: geoEventId, ...viewport });
    },
    [geoEventId, updateReportMapState],
  );

  const childGeoEvents = useMemo(
    () => ('childGeoEvents' in geoEvent ? geoEvent.childGeoEvents : []),
    [geoEvent],
  );
  const hasLocations = childGeoEvents.length > 0;

  const bbounds = useMemo(() => {
    if (zoom !== undefined) return undefined;

    if (!hasLocations) {
      const coordinates = new LngLat(geoEvent.lng, geoEvent.lat);

      const bounds = LngLatBoundsFromLngLat(coordinates);

      if (bounds.isEmpty()) return undefined;

      return bounds;
    }

    const geoEventCoords: CoordsPoint = [geoEvent.lng, geoEvent.lat];
    const childrenCoords = childGeoEvents.map(
      (childEvent) => [childEvent.lng, childEvent.lat] as CoordsPoint,
    );

    const radius = getFarthestDistance(geoEventCoords, childrenCoords);

    const boundingBox = calculateBoundingBox(geoEventCoords, radius);

    const coords = [
      new LngLat(boundingBox[0], boundingBox[1]),
      new LngLat(boundingBox[2], boundingBox[3]),
    ];

    const lngLatBounds = coords.reduce(
      (b, coord) => b.extend(coord),
      LngLatBoundsFromLngLat(coords[0]),
    );

    return lngLatBounds;
  }, [childGeoEvents, geoEvent.lat, geoEvent.lng, hasLocations, zoom]);

  // I'm not sure why we need to divide by 4 - it seems like half would be the correct amount?
  const bottomOffset =
    hiddenMapPixels && hiddenMapPixels !== 0 ? -hiddenMapPixels / 4 : 0;

  const center = {
    lat: geoEvent.lat ?? reportMapState.center.lat,
    lng: geoEvent.lng ?? reportMapState.center.lng,
  };

  const fitBoundsOptions: FitBoundsOptions | undefined = bbounds
    ? {
        padding: Math.max(Math.abs(bottomOffset) / 2, 8),
        maxZoom: hasLocations ? DEFAULT_VIEW_STATE.maxZoom : ZOOM_LEVELS.CITY,
        animate: false,
        offset: [0, bottomOffset],
      }
    : undefined;

  const selectedReport = useMemo(
    () => reports.find((report) => report.id === reportId),
    [reportId, reports],
  );

  const reportMaxAgeHours = getReportMapVisibilityHours(geoEvent.geoEventType);

  return (
    <Map
      id="geo-event-reports-map"
      bounds={bbounds}
      fitBoundsOptions={fitBoundsOptions}
      center={center}
      zoom={zoom ?? reportMapState.zoom}
      mapType={mapType}
      style={style}
    >
      {geoEvent.geoEventType === 'flooding' && (
        <>
          {showSelectedGeoEventLabel &&
            (geoEvent.isActive ? (
              <ActiveFloodingsLayer
                floodingEvents={[geoEvent as unknown as FloodingListItem]}
                selectedGeoEventId={geoEvent.id}
                interactive={false}
                isEdit
              />
            ) : (
              <InactiveFloodingsLayer
                floodingEvents={[geoEvent as unknown as FloodingListItem]}
                selectedGeoEventId={geoEvent.id}
                interactive={false}
                isEdit
              />
            ))}
          <Map.FloodingGeoEventMarker geoEvent={geoEvent} />
        </>
      )}
      {geoEvent.geoEventType === 'wildfire' && (
        <>
          {showSelectedGeoEventLabel && (
            <WildfiresLayer
              wildfires={[geoEvent as unknown as WildfireListItem]}
              selectedGeoEventId={geoEvent.id}
              interactive={false}
              isEdit
            />
          )}
          <Map.WildFireGeoEventMarker geoEvent={geoEvent} />
        </>
      )}
      {geoEvent.geoEventType === 'hazard' && (
        <Map.HazardGeoEventMarker geoEvent={geoEvent} />
      )}
      {!noMarkers &&
        (selectedReport ? (
          <Map.ReportMediaMarkers
            reports={[selectedReport]}
            interactive={false}
            showObfuscation={isRingReport(selectedReport)}
            maxAgeHours={reportMaxAgeHours}
          />
        ) : (
          <ReportsLayer
            reports={reports}
            interactive={false}
            geoEventType={geoEvent.geoEventType}
          />
        ))}
      <Map.MapEvents onViewportChange={onViewportChange} />
      {children}
    </Map>
  );
};
